import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";

export default function CandidateNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", linkedinUrl: "", location: "",
    visaStatus: "UNKNOWN", benchStatus: "AVAILABLE", entity: "BOTH",
    resumeUrl: "", skills: "", source: "",
  });

  function set<K extends keyof typeof form>(k: K, v: (typeof form)[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const payload: any = {
        name: form.name,
        visaStatus: form.visaStatus,
        benchStatus: form.benchStatus,
        entity: form.entity,
      };
      if (form.email)       payload.email = form.email;
      if (form.phone)       payload.phone = form.phone;
      if (form.linkedinUrl) payload.linkedinUrl = form.linkedinUrl;
      if (form.location)    payload.location = form.location;
      if (form.resumeUrl)   payload.resumeUrl = form.resumeUrl;
      if (form.source)      payload.source = form.source;
      if (form.skills) {
        payload.skills = form.skills.split(",").map((s) => s.trim()).filter(Boolean)
          .map((skill) => ({ skill, proficiency: "INTERMEDIATE" }));
      }
      await api.post("/candidates", payload);
      qc.invalidateQueries({ queryKey: ["/candidates"] });
      navigate("/candidates");
    } catch (err: any) {
      setError(err?.response?.data?.message ?? "Failed to create candidate.");
    } finally { setBusy(false); }
  }

  return (
    <>
      <PageHeader title="New Candidate" description="Add a candidate to the database." />
      <form onSubmit={submit} className="p-6">
        <Card className="max-w-2xl">
          <CardContent className="grid gap-4 p-6 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="name">Name *</Label>
              <Input id="name" required value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="linkedinUrl">LinkedIn</Label>
              <Input id="linkedinUrl" type="url" value={form.linkedinUrl} onChange={(e) => set("linkedinUrl", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="location">Location</Label>
              <Input id="location" value={form.location} onChange={(e) => set("location", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="source">Source</Label>
              <Input id="source" value={form.source} onChange={(e) => set("source", e.target.value)} />
            </div>
            <div>
              <Label htmlFor="visaStatus">Visa status</Label>
              <Select id="visaStatus" value={form.visaStatus} onChange={(e) => set("visaStatus", e.target.value)}>
                {["UNKNOWN","USC","GC","GC_EAD","H1B","H4_EAD","OPT","CPT","TN","L1","L2_EAD","OTHER"].map((v) =>
                  <option key={v} value={v}>{v.replace(/_/g, " ")}</option>)}
              </Select>
            </div>
            <div>
              <Label htmlFor="benchStatus">Bench status</Label>
              <Select id="benchStatus" value={form.benchStatus} onChange={(e) => set("benchStatus", e.target.value)}>
                <option value="AVAILABLE">Available</option>
                <option value="PLACED">Placed</option>
                <option value="NOT_LOOKING">Not looking</option>
                <option value="DNC">Do not contact</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="entity">Entity</Label>
              <Select id="entity" value={form.entity} onChange={(e) => set("entity", e.target.value)}>
                <option value="BOTH">Both</option>
                <option value="US">US</option>
                <option value="INDIA">India</option>
                <option value="GLOCALIZE">Glocalize</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="resumeUrl">Resume URL</Label>
              <Input id="resumeUrl" type="url" value={form.resumeUrl} onChange={(e) => set("resumeUrl", e.target.value)} />
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="skills">Skills (comma-separated)</Label>
              <Input id="skills" placeholder="React, Node, AWS, Python" value={form.skills} onChange={(e) => set("skills", e.target.value)} />
            </div>
          </CardContent>
          <div className="flex items-center justify-between border-t px-6 py-4">
            {error ? <p className="text-sm text-destructive">{error}</p> : <span />}
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => navigate("/candidates")}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.name}>{busy ? "Saving…" : "Create"}</Button>
            </div>
          </div>
        </Card>
      </form>
    </>
  );
}
